import type { ApiErrorBody } from "@velivo/shared";

/**
 * API client. Always calls a same-origin `/api/v1` — in production nginx routes
 * `/api/*` to the `api` container (DEPLOYMENT.md §2), and in dev Vite proxies
 * it, so no environment-specific base URL is needed anywhere.
 */
const BASE = "/api/v1";

const ACCESS_TOKEN_KEY = "echo.accessToken";
const REFRESH_TOKEN_KEY = "echo.refreshToken";

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = body.error.code;
    this.details = body.error.details;
  }

  /** Field-level messages, so a form can render errors next to the input that
   *  failed rather than as one generic banner (DESIGN_SYSTEM.md §4). */
  fieldErrors(): Record<string, string> {
    const fields = this.details?.fields;
    if (!Array.isArray(fields)) return {};
    const result: Record<string, string> = {};
    for (const entry of fields) {
      if (
        typeof entry === "object" &&
        entry !== null &&
        "path" in entry &&
        "message" in entry
      ) {
        result[String(entry.path)] = String(entry.message);
      }
    }
    return result;
  }
}

export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  set(accessToken: string, refreshToken?: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  /** Suppresses the one-shot refresh retry, used by the refresh call itself. */
  noRetry?: boolean;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/** Exchanges the refresh token for a new pair. Deduplicated so a burst of 401s
 *  doesn't fire several rotations and trip the reuse detector server-side. */
async function refreshSession(): Promise<boolean> {
  const refreshToken = tokenStore.refresh;
  if (!refreshToken) return false;

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) return false;
      const body = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
      };
      tokenStore.set(body.accessToken, body.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers share this result.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, auth = true, noRetry = false } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (auth && tokenStore.access) {
    headers.authorization = `Bearer ${tokenStore.access}`;
  }

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.ok) return (await parse(response)) as T;

  const parsed = (await parse(response)) as ApiErrorBody | null;
  const errorBody: ApiErrorBody = parsed?.error
    ? parsed
    : { error: { code: "NETWORK_ERROR", message: "Something went wrong." } };

  // A merely-expired access token is recoverable without bouncing the user to
  // the login screen. A stale session (role changed, signed out elsewhere) is
  // not — that one must re-authenticate.
  if (
    response.status === 401 &&
    errorBody.error.code === "TOKEN_EXPIRED" &&
    auth &&
    !noRetry
  ) {
    if (await refreshSession()) {
      return api<T>(path, { ...options, noRetry: true });
    }
  }

  throw new ApiRequestError(response.status, errorBody);
}
